
import { Color, LabelType } from '@/model';
import labels from '@/store/labels';
import ColorPicker from '@/components/ColorPicker.vue';
import Vue from 'vue';

interface EditableLabelType {
  code: string;
  color: Color;
  sortOrder?: number;
  selectable: boolean;
}

const defaultColor: Color = { r: 158, g: 158, b: 158 };

function createEditType(labelType: LabelType | undefined): EditableLabelType {
  if (labelType) {
    return {
      code: labelType.code,
      color: labelType.color,
      sortOrder: labelType.sortOrder,
      selectable: labelType.selectable,
    };
  }
  const maxSortOrder = labels.state.labelTypes
    .map((lt) => lt.sortOrder ?? 0)
    .reduce((max, so) => Math.max(max, so), 0);
  return {
    code: '',
    color: defaultColor,
    sortOrder: maxSortOrder + 10,
    selectable: true,
  };
}

export default Vue.extend({
  name: 'LabelTypeEditorDialog',

  components: {
    ColorPicker,
  },

  props: {
    value: { type: Boolean, default: false },
    labelType: { type: Object as () => LabelType | undefined },
  },

  data() {
    return {
      saving: false,
      editType: createEditType(this.labelType),
      rules: {
        required: (v: string) => !!v || 'Required',
      },
    };
  },

  computed: {
    isNew(): boolean {
      return !this.labelType;
    },
    previewColor(): string {
      const c = this.editType.color || defaultColor;
      return `rgba(${c.r}, ${c.g}, ${c.b}, 1)`;
    },
    isValid(): boolean {
      return !!this.editType.code;
    },
  },

  watch: {
    labelType: {
      handler(v: LabelType | undefined) {
        this.editType = createEditType(v);
      },
      immediate: true,
    },
    value(v: boolean) {
      if (v) {
        this.editType = createEditType(this.labelType);
      }
    },
  },

  methods: {
    close() {
      this.$emit('input', false);
    },
    async save() {
      this.saving = true;
      try {
        const typeToSave: LabelType = {
          code: this.editType.code,
          color: this.editType.color,
          sortOrder: this.editType.sortOrder,
          selectable: this.editType.selectable,
        };
        const saved = await labels.dispatch.saveLabelType(typeToSave);
        this.$emit('saved', saved);
        this.close();
      } finally {
        this.saving = false;
      }
    },
  },
});
